/**
 * app/components/DynamicAttributes.tsx
 * ------------------------------------
 * What: Renders a dynamic set of form fields based on attribute definitions for
 * the selected item subcategory.
 * Why: Supports flexible item schemas while keeping the form UI generic.
 */
import React from 'react';
import { View, Text, TextInput as RNTextInputBase, Switch, Platform, TouchableOpacity } from 'react-native';
import FormLabel from './FormLabel';
import FormTextInput from './FormTextInput';
import ThemedButton from './ThemedButton';
import { useTheme } from '../../lib/ThemeContext';

export type AttributeDataType =
  | 'text' | 'multiline_text' | 'number' | 'integer'
  | 'boolean' | 'date' | 'enum' | 'multiselect' | 'money' | 'url';

export type AttributeDefinition = {
  id: string;
  key: string;
  label: string;
  data_type: AttributeDataType;
  required: boolean;
  options?: any | null;
  ui_control?: string | null;
  help_text?: string | null;
  display_order: number;
};

type Props = {
  definitions: AttributeDefinition[];
  values: Record<string, any>;
  onChange: (next: Record<string, any>) => void;
};

// Use our wrapper for RN TextInput
const RNTextInput = FormTextInput as unknown as typeof RNTextInputBase;

export default function DynamicAttributes({ definitions, values, onChange }: Props) {
  const { theme } = useTheme();

  const setField = (key: string, value: any) => {
    const next = { ...values, [key]: value };
    // Clean up empty strings to nulls
    if (typeof next[key] === 'string' && next[key].trim() === '') {
      next[key] = null;
    }
    onChange(next);
  };

  if (!definitions || definitions.length === 0) {
    return null;
  }

  return (
    <View style={{ gap: 12 }}>
      {definitions.map((def) => {
        const key = def.key;
        const value = values?.[key] ?? '';
        const label = def.label || key;

        switch (def.data_type) {
          case 'boolean':
            return (
              <View key={def.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormLabel>{label}</FormLabel>
                <Switch
                  value={!!value}
                  onValueChange={(v) => setField(key, v)}
                  trackColor={{ false: theme.border, true: theme.accent }}
                  thumbColor={theme.card}
                />
              </View>
            );
          case 'number':
          case 'integer':
          case 'money':
            return (
              <View key={def.id}>
                <FormLabel>{label}{def.required ? ' *' : ''}</FormLabel>
                <RNTextInput
                  style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16 }}
                  keyboardType={Platform.OS === 'web' ? 'default' : 'numeric'}
                  value={value === null || value === undefined ? '' : String(value)}
                  onChangeText={(t) => {
                    if (def.data_type === 'integer') {
                      const cleaned = t.replace(/[^0-9-]/g, '');
                      setField(key, cleaned === '' ? null : parseInt(cleaned, 10));
                    } else {
                      const cleaned = t.replace(/[^0-9.-]/g, '');
                      setField(key, cleaned === '' ? null : parseFloat(cleaned));
                    }
                  }}
                />
              </View>
            );
          case 'multiline_text':
            return (
              <View key={def.id}>
                <FormLabel>{label}{def.required ? ' *' : ''}</FormLabel>
                <RNTextInput
                  multiline
                  numberOfLines={5}
                  style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16, minHeight: 80, textAlignVertical: 'top' }}
                  value={value ?? ''}
                  onChangeText={(t) => setField(key, t)}
                />
              </View>
            );
          case 'enum': {
            const choices: string[] = Array.isArray(def.options?.choices) ? def.options.choices : [];
            return (
              <View key={def.id}>
                <FormLabel>{label}{def.required ? ' *' : ''}</FormLabel>
                {/* Minimal select for web; text input fallback on native */}
                {Platform.OS === 'web' ? (
                  <select
                    value={value ?? ''}
                    onChange={(e) => setField(key, e.target.value || null)}
                    style={{ backgroundColor: theme.inputBackground as any, color: theme.text as any, borderColor: theme.border as any, borderWidth: 1 as any, borderRadius: 8 as any, padding: 12 as any, fontSize: 16 as any }}
                  >
                    <option value="">Select...</option>
                    {choices.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                ) : (
                  <RNTextInput
                    style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16 }}
                    value={value ?? ''}
                    onChangeText={(t) => setField(key, t)}
                    placeholder={`Select (${choices.join(', ')})`}
                  />
                )}
              </View>
            );
          }
          case 'date':
            return (
              <View key={def.id}>
                <FormLabel>{label}{def.required ? ' *' : ''}</FormLabel>
                {Platform.OS === 'web' ? (
                  <input
                    type="date"
                    value={value ? String(value).split('T')[0] : ''}
                    onChange={(e) => setField(key, e.target.value ? new Date(e.target.value).toISOString() : null)}
                    style={{ backgroundColor: theme.inputBackground as any, color: theme.text as any, border: `1px solid ${theme.border}` as any, borderRadius: 8 as any, padding: 12 as any, fontSize: 16 as any, width: '100%' as any }}
                  />
                ) : (
                  <RNTextInput
                    style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16 }}
                    value={value ? String(value).split('T')[0] : ''}
                    onChangeText={(t) => setField(key, t ? new Date(t).toISOString() : null)}
                    placeholder="YYYY-MM-DD"
                  />
                )}
              </View>
            );
          case 'url':
          case 'multiselect':
          case 'text':
          default:
            return (
              <View key={def.id}>
                <FormLabel>{label}{def.required ? ' *' : ''}</FormLabel>
                <RNTextInput
                  style={{ backgroundColor: theme.inputBackground, color: theme.text, borderColor: theme.border, borderWidth: 1, borderRadius: 8, paddingVertical: 12, paddingHorizontal: 14, fontSize: 16 }}
                  value={value ?? ''}
                  onChangeText={(t) => setField(key, t)}
                />
              </View>
            );
        }
      })}
    </View>
  );
}



